import * as React from 'react'
import { useRef } from 'react'

import { Box, Button, Radio } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import FormRow from 'modules/core/components/FormRow'
import DayPicker from 'modules/core/components/Inputs/DayPicker'
import Select from 'modules/core/components/Inputs/Select'
import TextInput from 'modules/core/components/Inputs/Text'
import Modal from 'modules/core/components/Modal'
import MultiInput from 'modules/core/components/MultiInput'
import i18n from 'modules/core/lib/i18n'

import { DurationUnit, PeriodType } from 'modules/timeoff/lib/BlockedPeriods/TimePeriod'
import type PeriodForm from 'modules/timeoff/lib/BlockedPeriods/TimePeriodForm'
import { blockedPeriodsDurationUnitOptions as durationUnitOptionsLabels } from 'modules/timeoff/lib/dynamicTranslations'

interface Props {
  timePeriodForm: PeriodForm
  closeModal: () => void
  onSave: (...args: any[]) => any
  isNew?: boolean
}

const FormModal = ({ timePeriodForm, closeModal, onSave, isNew = false }: Props) => {
  const durationUnitOptions = Object.keys(DurationUnit).map((value) => ({
    label: durationUnitOptionsLabels(value),
    value,
  }))

  const title = isNew
    ? i18n.t('settings.timeoff_blocked_periods.detail.time_periods.modal.new')
    : i18n.t('settings.timeoff_blocked_periods.detail.time_periods.modal.edit')

  const description = i18n.t(
    'settings.timeoff_blocked_periods.detail.time_periods.modal.description'
  )

  const saveButton = isNew
    ? i18n.t('settings.timeoff_blocked_periods.detail.time_periods.modal.create')
    : i18n.t('settings.timeoff_blocked_periods.detail.time_periods.modal.update')

  const onConfirm = () => onSave(timePeriodForm.data())

  const periodTypeField = useRef(timePeriodForm.get('periodType')).current

  return (
    <Modal
      illustration="document-edit"
      title={title}
      description={description}
      onClose={closeModal}
      footer={
        <>
          <Button onClick={closeModal} type="secondary">
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={!timePeriodForm.isValid} onClick={onConfirm}>
            {saveButton}
          </Button>
        </>
      }
    >
      <FormRow>
        <Box>
          <TextInput
            label={i18n.t('settings.timeoff_blocked_periods.fields.time_period.name')}
            field={timePeriodForm.get('name')}
            type="text"
            mandatory
          />
        </Box>
      </FormRow>

      <FormRow>
        <Radio.Wrap>
          <Box gap="s12">
            <Radio
              name="period_type_by_time_range"
              value={periodTypeField.value}
              onChange={(value) => periodTypeField.set(value)}
              description={i18n.t(
                'settings.timeoff_blocked_periods.detail.time_periods.modal.by_time_range'
              )}
              option={{
                label: i18n.t(
                  'settings.timeoff_blocked_periods.fields.time_period.period_type.by_time_range'
                ),
                value: PeriodType.by_time_range,
              }}
            >
              <Box gap="s16">
                <DayPicker
                  field={timePeriodForm.get('startOn')}
                  label={i18n.t('settings.timeoff_blocked_periods.fields.time_period.start_on')}
                  mandatory={timePeriodForm.byTimeRange}
                />
                <DayPicker
                  field={timePeriodForm.get('finishOn')}
                  label={i18n.t('settings.timeoff_blocked_periods.fields.time_period.finish_on')}
                  minDate={timePeriodForm.startOn}
                  mandatory={timePeriodForm.byTimeRange}
                />
              </Box>
            </Radio>
            <Radio
              name="period_type_by_contract_start_date"
              value={periodTypeField.value}
              onChange={(value) => periodTypeField.set(value)}
              description={i18n.t(
                'settings.timeoff_blocked_periods.detail.time_periods.modal.by_contract_start_date'
              )}
              option={{
                label: i18n.t(
                  'settings.timeoff_blocked_periods.fields.time_period.period_type.by_contract_start_date'
                ),
                value: PeriodType.by_contract_start_date,
              }}
            >
              <MultiInput
                label={i18n.t('settings.timeoff_blocked_periods.fields.time_period.duration')}
              >
                <TextInput
                  label={i18n.t('settings.timeoff_blocked_periods.fields.time_period.duration')}
                  hideLabel
                  field={timePeriodForm.get('duration')}
                  type="number"
                  min={1}
                  step="1"
                  mandatory={timePeriodForm.byContractStartDate}
                />
                <Box>
                  <Select
                    label={i18n.t(
                      'settings.timeoff_blocked_periods.fields.time_period.duration_units'
                    )}
                    hideLabel
                    field={timePeriodForm.get('durationUnit')}
                    options={durationUnitOptions}
                  />
                </Box>
              </MultiInput>
            </Radio>
          </Box>
        </Radio.Wrap>
      </FormRow>
    </Modal>
  )
}

export default observer(FormModal)
